<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-lg font-bold text-neutral-900">需求管理</h2>
        <div class="flex gap-3">
          <div class="relative">
            <input
              type="text"
              v-model="searchForm.keyword"
              @input="handleSearch"
              placeholder="搜索需求标题..."
              class="px-4 py-2 pl-10 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm w-64"
            />
            <i class="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"></i>
          </div>
          <select
            v-model="searchForm.categoryId"
            @change="handleSearch"
            class="px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          >
            <option value="">全部分类</option>
            <option v-for="item in categoryList" :value="item.id" :key="item.id">
              {{ item.title }}
            </option>
          </select>
          <select
            v-model="searchForm.status"
            @change="handleSearch"
            class="px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          >
            <option :value="-1">全部状态</option>
            <option :value="0">进行中</option>
            <option :value="1">已解决</option>
          </select>
          <button
            @click="refreshList"
            class="px-3 py-2 rounded-lg border border-neutral-300 hover:border-primary hover:text-primary transition-colors"
            title="刷新列表"
          >
            <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- 需求列表（两列并排） -->
      <div v-loading="loading" class="space-y-4">
        <div v-if="requestList.length > 0" class="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div
            v-for="request in requestList"
            :key="request.id"
            class="bg-neutral-50 rounded-xl border border-neutral-200 p-4 sm:p-6 hover:shadow-md transition-shadow duration-200"
          >
            <div class="flex flex-col sm:flex-row gap-6">
              <!-- 需求信息 -->
              <div class="flex-1 min-w-0 flex flex-col justify-between">
                <div>
                  <div class="flex justify-between items-start mb-2">
                    <h3 class="text-lg font-bold text-neutral-900 mb-1">
                      {{ request.title }}
                    </h3>
                    <span
                      class="text-xs px-2 py-1 rounded-full"
                      :class="{
                        'bg-yellow-100 text-yellow-700': request.status === 0,
                        'bg-green-100 text-green-700': request.status === 1,
                      }"
                    >
                      {{ getStatusText(request.status) }}
                    </span>
                  </div>
                  <div class="flex items-center text-sm text-neutral-500 mb-2">
                    <span class="mr-4">
                      <i class="fa fa-folder-o mr-1"></i>
                      {{ request.categoryName || getCategoryName(request.categoryId) }}
                    </span>
                    <span class="mr-4">
                      <i class="fa fa-user-o mr-1"></i>
                      {{ request.uname || '匿名用户' }}
                    </span>
                    <span>
                      <i class="fa fa-clock-o mr-1"></i>
                      {{ formatDate(request.createTime) }}
                    </span>
                  </div>
                  <p class="text-neutral-600 text-sm line-clamp-2 mb-3 whitespace-pre-line">
                    {{ request.content }}
                  </p>
                </div>

                <!-- 统计信息 -->
                <div class="flex items-center gap-4 text-sm text-neutral-500">
                  <span class="flex items-center">
                    <i class="fa fa-comment-o mr-1"></i>
                    {{ request.replyCount || 0 }} 回复
                  </span>
                </div>
              </div>

              <!-- 操作按钮 -->
              <div class="flex sm:flex-col justify-end sm:justify-center gap-3 border-t sm:border-t-0 sm:border-l border-neutral-200 pt-4 sm:pt-0 sm:pl-6">
                <button
                  v-if="request.status === 0"
                  @click="resolveRequest(request)"
                  class="flex-1 sm:flex-none px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium flex items-center justify-center"
                >
                  <i class="fa fa-check mr-2"></i>标记解决
                </button>
                <button
                  v-if="request.status === 1"
                  @click="reopenRequest(request)"
                  class="flex-1 sm:flex-none px-4 py-2 bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 transition-colors text-sm font-medium flex items-center justify-center"
                >
                  <i class="fa fa-refresh mr-2"></i>重新打开
                </button>
                <button
                  @click="viewDetail(request)"
                  class="flex-1 sm:flex-none px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm font-medium flex items-center justify-center"
                >
                  <i class="fa fa-eye mr-2"></i>详情
                </button>
                <button
                  @click="deleteRequest(request)"
                  class="flex-1 sm:flex-none px-4 py-2 bg-neutral-50 text-neutral-600 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors text-sm font-medium flex items-center justify-center"
                >
                  <i class="fa fa-trash-o mr-2"></i>删除
                </button>
              </div>
            </div>
          </div>

          <!-- 分页 -->
          <div class="flex justify-center mt-8">
            <el-pagination
              background
              layout="prev, pager, next"
              :total="total"
              :page-size="pageSize"
              :current-page="page"
              @current-change="handlePageChange"
            />
          </div>
        </div>

        <!-- 空状态 -->
        <div
          v-else
          class="bg-white rounded-xl border border-neutral-200 p-16 text-center"
        >
          <div class="w-20 h-20 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6 text-neutral-400">
            <i class="fa fa-inbox text-3xl"></i>
          </div>
          <h3 class="text-lg font-medium text-neutral-900 mb-2">
            暂无相关需求
          </h3>
          <p class="text-neutral-500 text-sm mb-6">
            没有找到符合条件的需求，试试其他筛选条件
          </p>
          <button
            @click="resetSearch"
            class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            重置筛选
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { globalState } from "@/state";
import { ElMessageBox, ElMessage } from "element-plus";

export default {
  name: "RequestManager",
  data() {
    return {
      requestList: [],
      categoryList: [],
      page: 1,
      pageSize: 10,
      total: 0,
      loading: false,
      searchForm: {
        keyword: "",
        categoryId: "",
        status: -1,
      },
      searchTimer: null,
    };
  },
  created() {
    this.getCategories();
    this.getRequestList();
  },
  methods: {
    // 获取分类列表
    async getCategories() {
      try {
        const res = await this.$axios({
          url: "demandcat/list",
        });
        if (res.code === 200) {
          this.categoryList = res.data || [];
        }
      } catch (error) {
        console.error("获取分类失败:", error);
      }
    },

    // 获取需求列表 - 使用与老师需求页面相同的接口
    async getRequestList() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize,
        };

        // 添加搜索条件
        if (this.searchForm.keyword) {
          params.searchKey = this.searchForm.keyword;
        }
        if (this.searchForm.categoryId) {
          params.categoryId = this.searchForm.categoryId;
        }
        if (this.searchForm.status !== -1) {
          params.status = this.searchForm.status;
        }

        const data = await this.$aesEncrypt(params);
        const res = await this.$axios({
          url: "demand/list",
          method: "POST",
          data: {
            data: data,
          },
        });

        if (res.code === 200) {
          this.requestList = res.data.list || [];
          this.total = res.data.total || 0;
        }
      } catch (error) {
        console.error("获取需求列表失败:", error);
        this.requestList = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    // 搜索（防抖）
    handleSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.getRequestList();
      }, 300);
    },

    // 刷新列表
    refreshList() {
      this.getRequestList();
    },

    // 分页切换
    handlePageChange(page) {
      this.page = page;
      this.getRequestList();
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        keyword: "",
        categoryId: "",
        status: -1,
      };
      this.page = 1;
      this.getRequestList();
    },

    // 获取状态文本
    getStatusText(status) {
      const map = { 0: "进行中", 1: "已解决" };
      return map[status] || "未知";
    },

    // 获取分类名称
    getCategoryName(id) {
      const category = this.categoryList.find((c) => c.id === id);
      return category ? category.title : "未知分类";
    },

    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      const h = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");
      return `${y}-${m}-${d} ${h}:${min}`;
    },

    // 标记解决
    resolveRequest(request) {
      ElMessageBox.confirm(
        `确定将需求"${request.title}"标记为已解决吗？`,
        "标记解决",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "success",
        }
      ).then(() => {
        this.$axios({
          url: `demand/update/status/${request.id}/1`,
          method: "GET",
        }).then((res) => {
          if (res.code === 200) {
            ElMessage.success("标记成功");
            this.getRequestList();
          } else {
            ElMessage.error(res.msg || "操作失败");
          }
        });
      });
    },

    // 重新打开需求
    reopenRequest(request) {
      ElMessageBox.confirm(
        `确定重新打开需求"${request.title}"吗？`,
        "重新打开",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      ).then(() => {
        this.$axios({
          url: `demand/update/status/${request.id}/0`,
          method: "GET",
        }).then((res) => {
          if (res.code === 200) {
            ElMessage.success("已重新打开");
            this.getRequestList();
          } else {
            ElMessage.error(res.msg || "操作失败");
          }
        });
      });
    },

    // 删除需求
    deleteRequest(request) {
      ElMessageBox.confirm(
        `确定删除需求"${request.title}"吗？删除后不可恢复`,
        "删除需求",
        {
          confirmButtonText: "删除",
          cancelButtonText: "取消",
          type: "error",
        }
      ).then(() => {
        this.$axios({
          url: `demand/${request.id}`,
          method: "DELETE",
        }).then((res) => {
          if (res.code === 200) {
            ElMessage.success("删除成功");
            this.getRequestList();
          } else {
            ElMessage.error(res.msg || "删除失败");
          }
        });
      });
    },

    // 查看详情
    viewDetail(request) {
      globalState.openRequestModal(request.id, this.categoryList);
    },
  },
};
</script>
